import { useAuth } from '../contexts/authContext';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { GetUserTrainingsPerActivity, GetUserTrainingsPerDayAndMonthStatistics, GetUserWorkoutsStatistics } from '../features/userTracking/services/userService';
import { UserWorkoutsStatistics } from '../features/userTracking/components/UserWorkoutsStatistics';
import { UserTrainingsPerActivityChart } from '../features/userTracking/components/UserTrainingsPerActivityChart';
import { UserTrainingsPerDayAndMonthChart } from '../features/userTracking/components/UserTrainingsPerDayAndMonthChart';
import { UserTrainingsPerActivityTable } from '../features/userTracking/components/UserTrainingsPerActivityTable';
import { UserTrainingsPerDayAndMonthTable } from '../features/userTracking/components/UserTrainingsPerDayAndMonthTable';
import { useSnackbar } from 'notistack';
import { Grid, Button, Box } from '@mui/material';

export default function UserStatisticsPage() {
  const auth = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [statisticsData, setStatisticsData] = useState({ isFetching: true, statistics: [] });
  const [statisticsByDayAndMonthData, setStatisticsByDayAndMonthData] = useState({ isFetching: true, statisticsDayAndMonth: [] });
  const [view, setView] = useState('chart');
  const [workoutsData, setWorkoutsData] = useState({ isFetching: true, statisticsWorkouts: [] });

  useEffect(() => {
    const fetchData = async (showSuccesMsg = true, showErrorMsg = true) => {
      try {
        const statistics = await GetUserTrainingsPerActivity(auth.user.id);
        if (showSuccesMsg) enqueueSnackbar(<FormattedMessage id='trainings.get.msg.success' defaultMessage='Trainings successfully fetched' />, {
          variant: 'success',
        });
        setStatisticsData({ isFetching: false, statistics });
      } catch (error) {
        if (showErrorMsg) enqueueSnackbar(<FormattedMessage id='trainings.get.msg.error' defaultMessage='Trainings fetch failed' />, {
          variant: 'error',
        });
        setStatisticsData({ isFetching: false, statistics: [] });
      }
    };

    const fetchSecondEndpointData = async () => {
      try {
        const statisticsDayAndMonth = await GetUserTrainingsPerDayAndMonthStatistics(auth.user.id);
        setStatisticsByDayAndMonthData({ isFetching: false, statisticsDayAndMonth });
      } catch (error) {
        console.error('Error fetching data from the second endpoint:', error);
        setStatisticsByDayAndMonthData({ isFetching: false, statisticsDayAndMonth: [] });
      }
    };

    const fetchWorkoutsData = async () => {
      try {
        const statisticsWorkouts = await GetUserWorkoutsStatistics(auth.user.id);
        setWorkoutsData({
          isFetching: false,
          cardioStatistics: statisticsWorkouts.cardioStatistics,
          strengthStatistics: statisticsWorkouts.strengthStatistics,
        });
      } catch (error) {
        console.error('Error fetching data from the workouts endpoint:', error);
        setWorkoutsData({ isFetching: false, cardioStatistics: [], strengthStatistics: [] });
      }
    };

    fetchData();
    fetchSecondEndpointData();
    fetchWorkoutsData();
  }, [auth.user.id, enqueueSnackbar]);

  const handleViewChange = (newView) => {
    setView(newView);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box display="flex" justifyContent="center" mb={2}>
          <Button
            variant={view === 'chart' ? 'contained' : 'outlined'}
            onClick={() => handleViewChange('chart')}
            style={{ marginRight: '8px' }}
          >
            <FormattedMessage id='userTracking.button.graph' defaultMessage='GROUP TRAINING GRAPH' />
          </Button>
          <Button
            variant={view === 'table' ? 'contained' : 'outlined'}
            onClick={() => handleViewChange('table')}
            style={{ marginRight: '8px' }}
          >
            <FormattedMessage id='userTracking.button.table' defaultMessage='GROUP TRAINING TABLE' />
          </Button>
          <Button
            variant={view === 'workouts' ? 'contained' : 'outlined'}
            onClick={() => handleViewChange('workouts')}
            style={{ marginLeft: '8px' }}
          >
            <FormattedMessage id='userTracking.button.workouts' defaultMessage='PERSONAL TRAINING' />
          </Button>
        </Box>
      </Grid>
      <Grid item xs={12}>
        {view === 'chart' && (
          <>
            <UserTrainingsPerActivityChart data={statisticsData.statistics} />
            <UserTrainingsPerDayAndMonthChart data={statisticsByDayAndMonthData.statisticsDayAndMonth} />
          </>
        )}
        {view === 'table' && (
          <>
            <UserTrainingsPerActivityTable data={statisticsData.statistics} />
            <Box sx={{ my: 2 }} />
            <UserTrainingsPerDayAndMonthTable data={statisticsByDayAndMonthData.statisticsDayAndMonth} />
          </>
        )}
        {view === 'workouts' && (
          <UserWorkoutsStatistics
            cardioStatistics={workoutsData.cardioStatistics}
            strengthStatistics={workoutsData.strengthStatistics}
          />
        )}
      </Grid>
    </Grid>
  );
}
